import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { 
  Table, 
  Button, 
  Select, 
  Alert, 
  Spin, 
  Tag, 
  Space, 
  Typography, 
  Input, 
  Card, 
  DatePicker,
  Slider,
  Row,
  Col
} from "antd";
import { PlusOutlined, EyeOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons";
import { useGetProposalsQuery, useGetMaxPriceQuery } from "../../redux/api/proposalsApi";
import { useGetProposalStatusesQuery } from "../../redux/api/referencesApi";
import { useGetLanguagesQuery } from "../../redux/api/languagesApi";
import { formatPrice } from "../../utils/currencyUtils";
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ProposalsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read URL params with defaults
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '10', 10);
  const searchParam = searchParams.get('search') || '';
  const statusIdParam = searchParams.get('status_id') || '';
  const createdFromParam = searchParams.get('created_from') || '';
  const createdToParam = searchParams.get('created_to') || '';
  const validFromParam = searchParams.get('valid_from') || '';
  const validToParam = searchParams.get('valid_to') || '';
  const priceMinParam = searchParams.get('price_min') || '';
  const priceMaxParam = searchParams.get('price_max') || '';
  const languageIdParam = searchParams.get('language_id') || '';
  const currencyCodeParam = searchParams.get('currency_code') || '';
  // Only use defaults if sort params don't exist in URL, not when they're cleared
  const sortBy = searchParams.has('sort_by') ? searchParams.get('sort_by') : 'created_at';
  const sortDirection = searchParams.has('sort_direction') ? searchParams.get('sort_direction') : 'desc';

  // Local state for search input (debounced)
  const [searchInput, setSearchInput] = useState(searchParam);

  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined || value === false) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Fetch maximum proposal price for slider range
  const { data: maxPriceData, isLoading: maxPriceLoading } = useGetMaxPriceQuery();
  const PRICE_RANGE_MAX = maxPriceData?.max_price || 1000000; // Fallback to 1M if not loaded yet

  // Local state for price range slider (updates immediately for UI, but API call happens on release)
  const [priceRangeLocal, setPriceRangeLocal] = useState(() => {
    const min = priceMinParam ? parseFloat(priceMinParam) : 0;
    const max = priceMaxParam ? parseFloat(priceMaxParam) : PRICE_RANGE_MAX;
    return [min, max];
  });

  // Sync local search input with URL param
  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  // Sync local price range with URL params
  useEffect(() => {
    const min = priceMinParam ? parseFloat(priceMinParam) : 0;
    const max = priceMaxParam ? parseFloat(priceMaxParam) : PRICE_RANGE_MAX;
    setPriceRangeLocal([min, max]);
  }, [priceMinParam, priceMaxParam, PRICE_RANGE_MAX]);

  // Debounce search input to URL params
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchParam) {
        updateSearchParams({ search: searchInput || null, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParam, updateSearchParams]);

  // Fetch proposals with filters
  const { data, isLoading, error } = useGetProposalsQuery({ 
    page,
    limit,
    search: searchParam || undefined,
    status_id: statusIdParam || undefined,
    created_from: createdFromParam || undefined,
    created_to: createdToParam || undefined,
    valid_from: validFromParam || undefined,
    valid_to: validToParam || undefined,
    price_min: priceMinParam || undefined,
    price_max: priceMaxParam || undefined,
    language_id: languageIdParam || undefined,
    currency_code: currencyCodeParam || undefined,
    sort_by: sortBy,
    sort_direction: sortDirection,
  });

  const { data: rawStatusOptions = [], isLoading: statusLoading, error: statusError } = useGetProposalStatusesQuery();
  const { data: languages = [], isLoading: languagesLoading } = useGetLanguagesQuery();

  // Map backend fields to expected format
  const statusOptions = rawStatusOptions.map((s) => ({
    id: s.proposal_status_id,
    name: s.status_name,
  }));

  const proposals = data?.proposals || [];
  const pagination = data?.pagination || {
    current_page: 1,
    total_pages: 1,
    total: 0,
    per_page: 10,
  };

  // Handle filter changes
  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleStatusChange = (value) => {
    updateSearchParams({ status_id: value || null, page: 1 });
  };

  const handleCreatedDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      updateSearchParams({ 
        created_from: dates[0].format('YYYY-MM-DD'), 
        created_to: dates[1].format('YYYY-MM-DD'),
        page: 1 
      });
    } else {
      updateSearchParams({ created_from: null, created_to: null, page: 1 });
    }
  };

  const handleValidDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      updateSearchParams({ 
        valid_from: dates[0].format('YYYY-MM-DD'), 
        valid_to: dates[1].format('YYYY-MM-DD'),
        page: 1 
      });
    } else {
      updateSearchParams({ valid_from: null, valid_to: null, page: 1 });
    }
  };

  // Handle slider change (immediate UI update)
  const handlePriceRangeChange = (value) => {
    if (value && value.length === 2) {
      setPriceRangeLocal(value);
    }
  };

  // Handle slider release (trigger API call)
  const handlePriceRangeAfterChange = (value) => {
    if (value && value.length === 2) {
      const [min, max] = value;
      // Only set filters if they're not at the default bounds
      updateSearchParams({ 
        price_min: min !== 0 ? min : null, 
        price_max: max !== PRICE_RANGE_MAX ? max : null, 
        page: 1 
      });
    } else {
      updateSearchParams({ price_min: null, price_max: null, page: 1 });
    }
  };

  const handleLanguageChange = (value) => {
    updateSearchParams({ language_id: value || null, page: 1 });
  };

  const handleCurrencyChange = (value) => {
    updateSearchParams({ currency_code: value || null, page: 1 });
  };

  const handlePageChange = (newPage) => {
    updateSearchParams({ page: newPage });
  };

  const handlePageSizeChange = (value) => {
    updateSearchParams({ limit: parseInt(value), page: 1 });
  };

  const handleResetFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const handleTableChange = (paginationCfg, filters, sorter, extra) => {
    // Pagination is handled separately by pagination.onChange -> handlePageChange.
    // Skipping here prevents this handler from overwriting the page back to 1.
    if (extra?.action !== 'sort') return;

    const sortInfo = Array.isArray(sorter) ? (sorter.length > 0 ? sorter[0] : null) : sorter;

    if (sortInfo && sortInfo.field && (sortInfo.order === 'ascend' || sortInfo.order === 'descend')) {
      const direction = sortInfo.order === 'ascend' ? 'asc' : 'desc';
      updateSearchParams({ sort_by: sortInfo.field, sort_direction: direction, page: 1 });
    } else {
      updateSearchParams({ sort_by: null, sort_direction: null, page: 1 });
    }
  };

  // Prepare date range values for DatePicker
  const createdDateRange = useMemo(() => {
    if (createdFromParam && createdToParam) {
      return [dayjs(createdFromParam), dayjs(createdToParam)];
    }
    return null;
  }, [createdFromParam, createdToParam]);

  const validDateRange = useMemo(() => {
    if (validFromParam && validToParam) {
      return [dayjs(validFromParam), dayjs(validToParam)];
    }
    return null;
  }, [validFromParam, validToParam]);


  // Helper: get status name and tag color
  const getStatusInfo = (proposal) => {
    const status = statusOptions.find((s) => s.id === proposal.proposal_status_id);
    if (!status) return { name: "Unknown", color: "default" };
    let color = "default";
    switch (status.name) {
      case "Draft":
        color = "default";
        break;
      case "Pending Approval":
        color = "processing";
        break;
      case "Under Negotiation":
        color = "blue";
        break;
      case "Accepted":
        color = "success";
        break;
      case "Rejected":
        color = "error";
        break;
      case "On Hold":
        color = "warning";
        break;
      case "Expired":
        color = "error";
        break;
      case "Converted to Order":
        color = "success";
        break;
      default:
        color = "default";
    }
    return { name: status.name, color };
  };

  // Table columns configuration
  const columns = [
    {
      title: "ID",
      dataIndex: "proposal_id",
      key: "proposal_id",
      width: 80,
      sorter: true,
      sortOrder: sortBy === 'proposal_id' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      sorter: true,
      sortOrder: sortBy === 'name' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (text) => text || "—",
    },
    {
      title: "Customer",
      dataIndex: "customer_full_name",
      key: "customer",
      sorter: true,
      sortOrder: sortBy === 'customer_full_name' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (text, record) => (
        <Link to={`/persons/${record.prospect_id}`}>{text}</Link>
      ),
    },
    {
      title: "Status",
      key: "status",
      sorter: true,
      sortOrder: sortBy === 'proposal_status_id' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (_, record) => {
        if (statusLoading) {
          return <Spin size="small" />;
        }
        if (statusError) {
          return <Tag color="error">Error</Tag>;
        }
        const statusInfo = getStatusInfo(record);
        return <Tag color={statusInfo.color}>{statusInfo.name}</Tag>;
      },
    },
    {
      title: "Total",
      dataIndex: "total_price",
      key: "total",
      sorter: true,
      sortOrder: sortBy === 'total_price' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (price) => formatPrice(price, 'CZK'),
    },
    {
      title: "Valid Until",
      dataIndex: "valid_until",
      key: "valid_until",
      sorter: true,
      sortOrder: sortBy === 'valid_until' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: sortBy === 'created_at' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      sorter: true,
      sortOrder: sortBy === 'updated_at' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: "Actions",
      key: "actions",
      width: '20%',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/proposals/${record.proposal_id}`)}
            title="View"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/proposals/${record.proposal_id}/edit`)}
            title="Edit"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>Loading proposals...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading proposals"
        description={error?.data?.message || error?.error || 'Failed to load proposals'}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <Title level={1} style={{ margin: 0 }}>Proposals</Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/proposals/add")}
          >
            Add New Proposal
          </Button>
        </Space>
      </div>

      {/* Filters Card */}
      <Card style={{ marginBottom: 24 }}>
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={[16, 16]}>
            {/* Search */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong>Search</Text>
              <Input
                placeholder="Search by customer name, email, or proposal ID..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                allowClear
                onClear={() => handleSearchChange('')}
                style={{ marginTop: 4 }}
              />
            </Col>

            {/* Status Filter */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong>Status</Text>
              <Select
                value={statusIdParam || null}
                onChange={handleStatusChange}
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All statuses"
                allowClear
                loading={statusLoading}
              >
                {statusOptions.map((status) => (
                  <Option key={status.id} value={status.id}>
                    {status.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Language Filter */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong>Language</Text>
              <Select
                value={languageIdParam || null}
                onChange={handleLanguageChange}
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All languages"
                allowClear
                loading={languagesLoading}
              >
                {languages.map((lang) => (
                  <Option key={lang.language_id} value={lang.language_id}>
                    {lang.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Currency Filter */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong>Currency</Text>
              <Select
                value={currencyCodeParam || null}
                onChange={handleCurrencyChange}
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All currencies"
                allowClear
              >
                <Option value="CZK">CZK (Czech Koruna)</Option>
                <Option value="EUR">EUR (Euro)</Option>
              </Select>
            </Col>

            {/* Created Date Range */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong>Created Date Range</Text>
              <RangePicker
                value={createdDateRange}
                onChange={handleCreatedDateRangeChange}
                style={{ width: '100%', marginTop: 4 }}
                format="YYYY-MM-DD"
              />
            </Col>

            {/* Valid Until Date Range */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong>Valid Until Range</Text>
              <RangePicker
                value={validDateRange}
                onChange={handleValidDateRangeChange}
                style={{ width: '100%', marginTop: 4 }}
                format="YYYY-MM-DD"
              />
            </Col>

            {/* Price Range Slider */}
            <Col xs={24} sm={24} md={12} lg={12}>
              <Text strong>Price Range</Text>
              {maxPriceLoading ? (
                <div style={{ marginTop: 8 }}>
                  <Spin size="small" />
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <Slider
                    range
                    min={0}
                    max={PRICE_RANGE_MAX}
                    step={10000}
                    value={priceRangeLocal}
                    onChange={handlePriceRangeChange}
                    onChangeComplete={handlePriceRangeAfterChange}
                    tooltip={{
                      formatter: (value) => formatPrice(value, 'CZK')
                    }}
                  />
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginTop: 12,
                    padding: '0 4px'
                  }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Min
                      </Text>
                      <Text strong style={{ fontSize: 14 }}>
                        {formatPrice(priceRangeLocal[0], 'CZK')}
                      </Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Max
                      </Text>
                      <Text strong style={{ fontSize: 14 }}>
                        {formatPrice(priceRangeLocal[1], 'CZK')}
                      </Text>
                    </div>
                  </div>
                </div>
              )}
            </Col>

            {/* Items per page */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong>Show</Text>
              <Select
                value={limit}
                onChange={handlePageSizeChange}
                style={{ width: '100%', marginTop: 4 }}
              >
                <Option value={5}>5 per page</Option>
                <Option value={10}>10 per page</Option>
                <Option value={25}>25 per page</Option>
                <Option value={50}>50 per page</Option>
                <Option value={100}>100 per page</Option>
              </Select>
            </Col>

            {/* Reset Filters */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>&nbsp;</Text>
              <Button onClick={handleResetFilters} style={{ marginTop: 4 }}>
                Reset Filters
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Results count */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          Showing {proposals.length} of {pagination.total} proposals
          {searchParam && ` matching "${searchParam}"`}
          {statusIdParam && ` (status: ${statusOptions.find(s => s.id === parseInt(statusIdParam))?.name || statusIdParam})`}
        </Text>
      </div>

      {proposals.length === 0 ? (
        <Alert
          message="No proposals found"
          description={searchParam || statusIdParam ? 'No proposals match your filters.' : 'No proposals available.'}
          type="info"
          showIcon
        />
      ) : (
        <Table
          columns={columns}
          dataSource={proposals.map((proposal) => ({
            ...proposal,
            key: proposal.proposal_id,
          }))}
          pagination={{
            current: pagination.current_page,
            total: pagination.total,
            pageSize: limit,
            showSizeChanger: false,
            onChange: handlePageChange,
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} proposals`,
          }}
          loading={isLoading}
          size="middle"
          rowKey="proposal_id"
          onChange={handleTableChange}
        />
      )}
    </div>
  );
};

export default ProposalsPage;
